import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { userProgress as initialProgress, reviewQueue as initialReviewQueue, achievements as initialAchievements } from '../data/courseData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// In-memory fallback storage (same as AuthContext)
const memoryStorage = {
  data: {},
  getItem(key) {
    return this.data[key] || null;
  },
  setItem(key, value) {
    this.data[key] = value;
  },
  removeItem(key) {
    delete this.data[key];
  }
};

// Check if localStorage is available
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// Check if sessionStorage is available
const isSessionStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// Helper function to get auth token from ALL storage options (including memory fallback)
const getAuthToken = () => {
  try {
    // Try localStorage first
    if (isLocalStorageAvailable()) {
      const token = localStorage.getItem('fuse_auth_token');
      if (token) return token;
    }
  } catch (e) {
    console.warn('localStorage access error:', e);
  }
  
  try {
    // Try sessionStorage
    if (isSessionStorageAvailable()) {
      const token = sessionStorage.getItem('fuse_auth_token');
      if (token) return token;
    }
  } catch (e) {
    console.warn('sessionStorage access error:', e);
  }
  
  // Try memory storage as fallback
  return memoryStorage.getItem('fuse_auth_token');
};

// Safe localStorage functions with memory fallback
const safeGetItem = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return memoryStorage.getItem(key);
  } catch (e) {
    return memoryStorage.getItem(key);
  }
};

const safeSetItem = (key, value) => {
  try {
    memoryStorage.setItem(key, value); // Always save to memory as backup
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    console.warn('localStorage not available, using memory storage');
  }
};

const safeRemoveItem = (key) => {
  try {
    memoryStorage.removeItem(key);
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn('localStorage not available');
  }
};

export const AppProvider = ({ children }) => {
  // Current course ID - will be updated when user changes course
  const [currentCourseId, setCurrentCourseId] = useState(() => {
    const savedUser = safeGetItem('fuse_user_data');
    let courseId = 'pt-en';
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        courseId = user.selected_course || 'pt-en';
      } catch {
        // use default
      }
    }
    return courseId;
  });

  // Get initial course data
  const getInitialCourseData = (courseId) => {
    return { tracks: [], lessons: {} };
  };

  // User progress state
  const [userProgress, setUserProgress] = useState(() => {
    const saved = safeGetItem('userProgress');
    return saved ? JSON.parse(saved) : initialProgress;
  });

  // Tracks state - always load fresh from course data based on selected course
  const [tracks, setTracks] = useState(() => {
    const savedUser = safeGetItem('fuse_user_data');
    let courseId = 'pt-en';
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        courseId = user.selected_course || 'pt-en';
      } catch {
        // use default
      }
    }
    const saved = safeGetItem(`tracks_${courseId}`);
    const courseData = getInitialCourseData(courseId);
    return saved ? JSON.parse(saved) : courseData.tracks;
  });

  // Lessons state - always load fresh from course data based on selected course
  const [lessons, setLessons] = useState(() => {
    const savedUser = safeGetItem('fuse_user_data');
    let courseId = 'pt-en';
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        courseId = user.selected_course || 'pt-en';
      } catch {
        // use default
      }
    }
    const saved = safeGetItem(`lessons_${courseId}`);
    const courseData = getInitialCourseData(courseId);
    return saved ? JSON.parse(saved) : courseData.lessons;
  });

  // Review queue state
  const [reviewQueue, setReviewQueue] = useState(() => {
    const saved = safeGetItem('reviewQueue');
    return saved ? JSON.parse(saved) : initialReviewQueue;
  });

  // Achievements state
  const [achievements, setAchievements] = useState(() => {
    const saved = safeGetItem('achievements');
    return saved ? JSON.parse(saved) : initialAchievements;
  });

  // Update tracks and lessons when course changes - fetch from API
  useEffect(() => {
    const fetchCourseData = async () => {
      console.log('AppContext: Fetching course data for:', currentCourseId);
      
      try {
        // Fetch tracks from API
        const tracksResponse = await axios.get(`${API_URL}/api/courses/${currentCourseId}/tracks`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        
        if (tracksResponse.data?.tracks) {
          const apiTracks = tracksResponse.data.tracks;
          console.log('AppContext: Got tracks from API:', apiTracks.length);
          setTracks(apiTracks);
          safeSetItem(`tracks_${currentCourseId}`, JSON.stringify(apiTracks));
          
          // Fetch lessons for each track
          const lessonsMap = {};
          for (const track of apiTracks) {
            try {
              const lessonsResponse = await axios.get(`${API_URL}/api/tracks/${track.id}/lessons`, {
                withCredentials: true,
                headers: { 'Authorization': `Bearer ${getAuthToken()}` }
              });
              
              if (lessonsResponse.data?.lessons) {
                lessonsMap[track.id] = lessonsResponse.data.lessons;
              }
            } catch (err) {
              console.warn(`Failed to fetch lessons for track ${track.id}:`, err);
              lessonsMap[track.id] = [];
            }
          }
          
          console.log('AppContext: Got lessons from API for tracks:', Object.keys(lessonsMap));
          setLessons(lessonsMap);
          safeSetItem(`lessons_${currentCourseId}`, JSON.stringify(lessonsMap));
        }
      } catch (error) {
        console.error('AppContext: Error fetching course data from API:', error);
        // Fallback to local data
        const savedTracks = safeGetItem(`tracks_${currentCourseId}`);
        const savedLessons = safeGetItem(`lessons_${currentCourseId}`);
        
        setTracks(savedTracks ? JSON.parse(savedTracks) : []);
        setLessons(savedLessons ? JSON.parse(savedLessons) : {});
      }
    };
    
    fetchCourseData();
  }, [currentCourseId]);

  // Listen for user data changes to update course
  useEffect(() => {
    const checkUserCourse = () => {
      const savedUser = safeGetItem('fuse_user_data');
      if (savedUser) {
        try {
          const user = JSON.parse(savedUser);
          if (user.selected_course && user.selected_course !== currentCourseId) {
            setCurrentCourseId(user.selected_course);
          }
        } catch {
          // Ignore parse errors
        }
      }
    };
    
    // Check initially and set up interval
    checkUserCourse();
    const interval = setInterval(checkUserCourse, 1000);
    return () => clearInterval(interval);
  }, [currentCourseId]);

  // Save to localStorage when state changes
  useEffect(() => {
    safeSetItem('userProgress', JSON.stringify(userProgress));
  }, [userProgress]);

  // Only save tracks if they match the current course
  useEffect(() => {
    // Verify tracks belong to current course before saving
    if (tracks && tracks.length > 0) {
      const expectedPrefix = currentCourseId === 'pt-en' ? 'daily-life' : 
                            currentCourseId === 'pt-ja' ? 'daily-life-ja' :
                            currentCourseId === 'ja-en' ? 'daily-life-en' :
                            currentCourseId === 'ja-pt' ? 'daily-life-pt' : 'daily-life';
      
      const firstTrackId = tracks[0]?.id || '';
      if (firstTrackId.startsWith(expectedPrefix.split('-')[0])) {
        safeSetItem(`tracks_${currentCourseId}`, JSON.stringify(tracks));
      }
    }
  }, [tracks, currentCourseId]);

  // Only save lessons if they match the current course  
  useEffect(() => {
    if (lessons && Object.keys(lessons).length > 0) {
      const firstKey = Object.keys(lessons)[0] || '';
      const expectedPrefix = currentCourseId === 'pt-en' ? 'daily-life' : 
                            currentCourseId === 'pt-ja' ? 'daily-life-ja' :
                            currentCourseId === 'ja-en' ? 'daily-life-en' :
                            currentCourseId === 'ja-pt' ? 'daily-life-pt' : 'daily-life';
      
      if (firstKey.startsWith(expectedPrefix.split('-')[0])) {
        console.log('AppContext: Saving lessons for course:', currentCourseId);
        safeSetItem(`lessons_${currentCourseId}`, JSON.stringify(lessons));
      }
    }
  }, [lessons, currentCourseId]);

  useEffect(() => {
    safeSetItem('reviewQueue', JSON.stringify(reviewQueue));
  }, [reviewQueue]);

  useEffect(() => {
    safeSetItem('achievements', JSON.stringify(achievements));
  }, [achievements]);

  // Actions
  const addXP = (amount) => {
    setUserProgress(prev => ({
      ...prev,
      totalXP: prev.totalXP + amount,
    }));
  };

  const incrementStreak = () => {
    setUserProgress(prev => ({
      ...prev,
      currentStreak: prev.currentStreak + 1,
    }));
  };

  const updateDailyProgress = (minutes) => {
    setUserProgress(prev => ({
      ...prev,
      dailyProgress: Math.min(prev.dailyProgress + minutes, prev.dailyGoal),
    }));
  };

  const completeLesson = useCallback(async (trackId, lessonId, xpEarned) => {
    // Update local state first for immediate feedback
    setLessons(prev => ({
      ...prev,
      [trackId]: prev[trackId]?.map(lesson =>
        lesson.id === lessonId
          ? { ...lesson, completed: true, current: false }
          : lesson
      ) || [],
    }));

    // Update track progress locally
    setTracks(prev =>
      prev.map(track =>
        track.id === trackId
          ? { ...track, completedLessons: track.completedLessons + 1 }
          : track
      )
    );

    // Update user progress locally
    setUserProgress(prev => ({
      ...prev,
      totalXP: prev.totalXP + xpEarned,
      lessonsCompleted: prev.lessonsCompleted + 1,
    }));

    // Sync with backend - save to database
    try {
      const token = getAuthToken();
      console.log('Saving progress to backend. Token exists:', !!token, 'Lesson:', lessonId);
      
      // Make request with both cookie and token for maximum compatibility
      const response = await axios.post(`${API_URL}/api/progress/lesson-complete`, {
        lesson_id: lessonId,
        xp_earned: xpEarned
      }, {
        withCredentials: true, // Send cookies
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      console.log('Progress saved to database successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to save progress to backend:', error?.response?.data || error?.message || error);
      // Local state is already updated, so user still sees progress
      // But we should notify that sync failed
      return { success: false, error: 'Failed to sync with server' };
    }
  }, []);

  const addToReviewQueue = (phrase) => {
    setReviewQueue(prev => [...prev, phrase]);
  };

  const updateReviewStrength = (phraseId, newStrength) => {
    setReviewQueue(prev =>
      prev.map(phrase =>
        phrase.id === phraseId
          ? { ...phrase, strength: newStrength, lastReview: 'Just now' }
          : phrase
      )
    );
  };

  const earnBadge = (badgeId) => {
    setUserProgress(prev => ({
      ...prev,
      badges: prev.badges.map(badge =>
        badge.id === badgeId
          ? { ...badge, earned: true }
          : badge
      ),
    }));
  };

  const updateAchievement = (achievementId, progress) => {
    setAchievements(prev =>
      prev.map(achievement =>
        achievement.id === achievementId
          ? {
              ...achievement,
              progress,
              earned: progress >= 100,
              earnedDate: progress >= 100 ? new Date().toISOString().split('T')[0] : null,
            }
          : achievement
      )
    );
  };

  const resetProgress = async () => {
    safeRemoveItem('userProgress');
    safeRemoveItem(`tracks_${currentCourseId}`);
    safeRemoveItem(`lessons_${currentCourseId}`);
    safeRemoveItem('reviewQueue');
    safeRemoveItem('achievements');
    setUserProgress(initialProgress);
    
    // Fetch fresh data from API instead of local file
    try {
        const tracksResponse = await axios.get(`${API_URL}/api/courses/${currentCourseId}/tracks`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (tracksResponse.data?.tracks) {
             setTracks(tracksResponse.data.tracks);
        } else {
             setTracks([]);
        }
    } catch (e) {
        setTracks([]);
    }
    setLessons({});
    setReviewQueue(initialReviewQueue);
    setAchievements(initialAchievements);
  };

  // Function to switch course
  const switchCourse = (newCourseId) => {
    setCurrentCourseId(newCourseId);
  };

  const value = {
    userProgress,
    tracks,
    lessons,
    reviewQueue,
    achievements,
    currentCourseId,
    addXP,
    incrementStreak,
    updateDailyProgress,
    completeLesson,
    addToReviewQueue,
    updateReviewStrength,
    earnBadge,
    updateAchievement,
    resetProgress,
    switchCourse,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
